import { Button, Divider, lighten, Stack, Typography } from "@mui/material";
import Header from "../Headers/Header";
import Product from "../Product";
import { useSelectedProduct } from "src/hooks/useSelectedProduct";
import { handleFocusIntoView } from "src/utils/focusIntoView";
import Subheader from "../Headers/Subheader";
import { useIsMobile } from "src/hooks/useIsMobile";
import InformationArray from "../InformationArray";
import { useTranslation } from "react-i18next";
import PaymentProcessor from "../PaymentProcessor";
import { useMemo } from "react";
import { useProduct } from "src/context/ProductProvider";
import { theme } from "src/assets/theme";

const disclaimerStyle: Record<string, object> = {
  "b-ot": {
    alignSelf: "end",
  },
  "sl-ot": {
    color: theme.palette.primary.main,
    fontStyle: "italic",
    fontWeight: 600,
  },
};

const SupplementsPaymentInformation = () => {
  const isMobile = useIsMobile();
  const [selectedProduct] = useSelectedProduct();
  const { t } = useTranslation();
  const page = "payment-information";
  const slug = useProduct((p) => p.slug);
  const billingModel = useProduct((p) => p.billing_model);

  const defaultProductPrice = useMemo(() => {
    if (selectedProduct == null) {
      return "0";
    }

    const price = Math.max(
      selectedProduct.amount,
      selectedProduct.amount_limit ?? 0,
    );
    return price.toLocaleString();
  }, [selectedProduct]);

  const saving = useMemo(() => {
    if (selectedProduct == null) return;
    const defaultPrice = Number(defaultProductPrice.replace(",", ""));

    const comparedPrice = selectedProduct.competitor_price || 0;

    return comparedPrice - defaultPrice;
  }, [defaultProductPrice, selectedProduct]);

  const paymentDisclaimer = useMemo(() => {
    if (slug === "sl-ot") {
      return t(`${page}.${billingModel}.disclaimer`);
    }

    return t(`${page}.hsaDisclaimer`);
  }, [billingModel, slug, t]);

  return (
    <Stack gap={3}>
      <Stack gap={2}>
        <Stack gap={3}>
          <Header />
          <Product product={selectedProduct} showFsaHsa />
        </Stack>
        <Stack gap={2} onClick={() => handleFocusIntoView("payment-input")}>
          <Typography variant={isMobile ? "body1" : "h6"} fontWeight={600}>
            <Subheader />
          </Typography>
          <InformationArray />
        </Stack>
      </Stack>
      <Stack gap={3}>
        <Stack gap={4}>
          <Stack gap={2}>
            <Stack gap={1}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography fontSize={"22px"} fontWeight={600}>
                  {t(`${page}.total`)}
                </Typography>
                <Typography fontSize={"22px"} fontWeight={600}>
                  ${defaultProductPrice}
                </Typography>
              </Stack>
              <Typography sx={disclaimerStyle[slug]}>
                {paymentDisclaimer}
              </Typography>
            </Stack>
            {saving != null && saving > 0 ? (
              <Button
                variant="text"
                sx={{
                  backgroundColor: lighten(theme.palette.secondary.main, 0.3),
                  boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
                  color: "#ffffff",
                  fontSize: "16px",
                  fontWeight: 700,
                  borderRadius: "10px",
                  textAlign: "center",
                }}
                onClick={() => handleFocusIntoView("payment-input")}
              >
                {t(`${page}.totalSavings`, {
                  saving,
                })}
              </Button>
            ) : null}
            <Divider sx={{ position: "relative", top: 15 }} />
          </Stack>
          {selectedProduct ? (
            <PaymentProcessor
              product={selectedProduct}
              coupon={null}
              dueToday={Number(defaultProductPrice)}
            />
          ) : null}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default SupplementsPaymentInformation;
